import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { workerCertificationService } from '../../../services/workerCertificationService';
import { useLanguage } from '../../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../../hooks/useSubscriptionReadOnly';

const CERT_TYPES = [
  'CSCS', 'IPAF', 'PASMA', 'First Aid', 'Plant Operator', 'Scaffold', 'Asbestos Awareness', 'Other',
];

const EditCertificationModal = ({ certification, onClose, onSaved }) => {
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [form, setForm] = useState({
    certificateType: certification?.certificate_type || '',
    certificateNumber: certification?.certificate_number || '',
    expiryDate: certification?.expiry_date || '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileChange = (e) => {
    const selected = e?.target?.files?.[0];
    if (selected) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(selected?.type)) {
        setError('Only PDF and image files are allowed.');
        return;
      }
      setFile(selected);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (subscriptionReadOnly) {
      setError('Your subscription is read-only. Editing certifications is disabled.');
      return;
    }
    if (!form?.certificateType) { setError('Please select a certificate type.'); return; }
    if (!form?.expiryDate) { setError('Please enter an expiry date.'); return; }

    try {
      setSaving(true);
      let filePath = certification?.file_path;

      if (file) {
        const uploadResult = await workerCertificationService?.uploadCertificateFile(
          file,
          certification?.worker_user_id
        );
        if (uploadResult?.error) throw uploadResult.error;
        filePath = uploadResult?.data?.path;
      }

      const result = await workerCertificationService?.updateCertification(certification?.id, {
        certificateType: form?.certificateType,
        certificateNumber: form?.certificateNumber,
        expiryDate: form?.expiryDate,
        filePath,
      });

      if (result?.error) throw result.error;
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[EditCertificationModal] update failed:', err);
      setError(err?.message || 'Failed to update certification. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-elevation-4 w-full max-w-lg border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground font-heading">{t('certEditTitle')}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t('certColCertType')} <span className="text-destructive">*</span></label>
            <select
              value={form?.certificateType}
              onChange={e => handleChange('certificateType', e?.target?.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {CERT_TYPES?.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t('certColCertNumber')}</label>
            <input
              type="text"
              value={form?.certificateNumber}
              onChange={e => handleChange('certificateNumber', e?.target?.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t('certColExpiryDate')} <span className="text-destructive">*</span></label>
            <input
              type="date"
              value={form?.expiryDate}
              onChange={e => handleChange('expiryDate', e?.target?.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t('certReplaceFile')} <span className="text-muted-foreground text-xs">({t('certOptional')})</span></label>
            <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-accent/50 transition-colors">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileChange} className="hidden" />
              {file ? (
                <span className="text-sm text-foreground font-medium">{file?.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">{t('certClickToReplace')}</span>
              )}
            </label>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={saving}>{t('cancel')}</Button>
            <Button variant="primary" type="submit" loading={saving} disabled={subscriptionReadOnly}>{t('certSaveChanges')}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCertificationModal;
